import { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from "react";
import { useTenant } from "@/hooks/useTenant";

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image_url: string | null;
  requires_prescription: boolean;
  stock: number;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, "quantity">, quantity?: number) => { capped: boolean; maxAvailable: number };
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  total: number;
  itemCount: number;
  notes: string;
  setNotes: (notes: string) => void;
  hasPrescriptionItems: boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

function getCartKey(tenantId: string | null) {
  return tenantId ? `megastore_cart_${tenantId}` : "megastore_cart_guest";
}
function getNotesKey(tenantId: string | null) {
  return tenantId ? `megastore_cart_notes_${tenantId}` : "megastore_cart_notes_guest";
}

export function CartProvider({ children }: { children: ReactNode }) {
  const { tenantId } = useTenant();

  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const stored = localStorage.getItem(getCartKey(tenantId));
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [notes, setNotes] = useState(() => localStorage.getItem(getNotesKey(tenantId)) ?? "");

  // Re-load cart when tenant changes
  useEffect(() => {
    try {
      const stored = localStorage.getItem(getCartKey(tenantId));
      setItems(stored ? JSON.parse(stored) : []);
    } catch {
      setItems([]);
    }
    setNotes(localStorage.getItem(getNotesKey(tenantId)) ?? "");
  }, [tenantId]);

  useEffect(() => {
    localStorage.setItem(getCartKey(tenantId), JSON.stringify(items));
  }, [items, tenantId]);

  useEffect(() => {
    localStorage.setItem(getNotesKey(tenantId), notes);
  }, [notes, tenantId]);

  const addItem = useCallback((item: Omit<CartItem, "quantity">, quantity = 1): { capped: boolean; maxAvailable: number } => {
    let capped = false;
    const maxAvailable = item.stock;

    setItems((prev) => {
      const existing = prev.find((i) => i.id === item.id);
      if (existing) {
        const desired = existing.quantity + quantity;
        const final_ = Math.min(desired, item.stock);
        if (final_ <= existing.quantity || desired > item.stock) capped = true;
        return prev.map((i) =>
          i.id === item.id ? { ...i, quantity: final_ } : i
        );
      }
      const final_ = Math.min(quantity, item.stock);
      if (quantity > item.stock) capped = true;
      return [...prev, { ...item, quantity: final_ }];
    });

    return { capped, maxAvailable };
  }, []);

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const updateQuantity = useCallback((id: string, quantity: number) => {
    if (quantity <= 0) {
      setItems((prev) => prev.filter((i) => i.id !== id));
    } else {
      setItems((prev) =>
        prev.map((i) => (i.id === id ? { ...i, quantity: Math.min(quantity, i.stock) } : i))
      );
    }
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setNotes("");
  }, []);

  const total = useMemo(() => items.reduce((sum, i) => sum + i.price * i.quantity, 0), [items]);
  const itemCount = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items]);
  const hasPrescriptionItems = useMemo(() => items.some((i) => i.requires_prescription), [items]);

  const value = useMemo<CartContextType>(
    () => ({ items, addItem, removeItem, updateQuantity, clearCart, total, itemCount, notes, setNotes, hasPrescriptionItems }),
    [items, addItem, removeItem, updateQuantity, clearCart, total, itemCount, notes, setNotes, hasPrescriptionItems]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
